import React, { Fragment, useState, useEffect, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AuthContext } from "../contexts/auth";
import { getDayCalendarWorkingHour } from '../services/api_calendar';
import Swal from 'sweetalert2';
import moment from 'moment';
import Fetching from './fetching';
import { MapPinIcon } from '@heroicons/react/24/outline';
import LocationMap from './location_map';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import RunningWithErrorsOutlinedIcon from '@mui/icons-material/RunningWithErrorsOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
'& .MuiTooltip-tooltip': {
    backgroundColor: '#a3a3a3',
    color: '#FFFFFF',
    fontSize: '0.8rem',
},
});


const DayDetailsModal = ({ isOpen, onClose, formattedDay, employee: dipendente }) => {
    const { token, profile } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isForbidden, setIsForbidden] = useState(false);
    const [employeeDayDetails, setEmployeeDayDetails] = useState(null);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [locationData, setLocationData] = useState(null);

    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');

    const hasViewTimbraturePermission =
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');

    const hasViewShiftsPermission =
        profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.view_shifts_all') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_all');

    const isEmployee = profile.customer_type === 'employee';
    
    if (!isOpen) return null;

    const openLocationMap = (inout) => {
        // Creiamo un array di posizioni
        const locations = [];
    
        if (inout.checkin_lat && inout.checkin_lng) {
            locations.push({
                latitude: inout.checkin_lat,
                longitude: inout.checkin_lng,
                accuracy: inout.checkin_accuracy || 50,
                type: 'checkin',
            });
        }
    
        if (inout.checkout_lat && inout.checkout_lng) {
            locations.push({
                latitude: inout.checkout_lat,
                longitude: inout.checkout_lng,
                accuracy: inout.checkout_accuracy || 50,
                type: 'checkout',
            });
        }
        
        setLocationData(locations);
        setIsLocationModalOpen(true);
    };
    
    const closeLocationModal = () => {
        setIsLocationModalOpen(false);
        setLocationData(null);
    };
    

    useEffect(() => {
        const fetchData = async () => {
            if (isOpen && dipendente?.id && formattedDay && token) {
                const employeeId = dipendente.id;

                try {
                    setIsLoading(true);
                    setIsError(false);
                    setIsForbidden(false);

                    const calendarResponse = await getDayCalendarWorkingHour(
                        token,
                        formattedDay,
                        null,
                        [employeeId],
                        1, 
                        10
                    );

                    const modifiedCalendarResponse = calendarResponse.results.map((entry) => ({
                        ...entry,
                        days: entry.days.map((day) => ({
                            ...day,
                            justification_requests:
                                isEmployee && !hasManageJustificationPermission
                                    ? []
                                    : day.justification_requests,
                            inouts: isEmployee && !hasViewTimbraturePermission ? [] : day.inouts,
                            shifts: day.shifts.map((shift) => ({
                                ...shift,
                                time_slots:
                                    isEmployee && !hasViewShiftsPermission ? [] : shift.time_slots,
                            })),
                        })),
                    }));

                    const employeeDayDetails = modifiedCalendarResponse.find(employee => employee.id === employeeId);
                    const dayDetails = employeeDayDetails?.days.find(day => day.date === formattedDay);

                    setEmployeeDayDetails(dayDetails);

                } catch (error) {
                    console.error(error);
                    if (error.response && error.response.status === 403) {
                        setIsForbidden(true);
                    } else {
                        setIsError(true);
                    }
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchData();
    }, [isOpen, dipendente?.id, formattedDay, token, profile.employee?.permissions]);

    if (isLoading) return <Fetching />;
    if (isError) return <div>Error occurred while fetching data</div>;
    if (isForbidden) return <div>Access Denied</div>;

    const { time_slots, justification_requests, inouts } = employeeDayDetails || {};

    if (!isOpen || !employeeDayDetails) return null;
    
    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 overflow-y-auto top-10" style={{ zIndex: '400' }} onClose={onClose}>
                <div className="flex items-start justify-center min-h-screen px-4 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                    </Transition.Child>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-2/3 sm:w-2/3 mt-20 sm:mt-30 top-0">
                            <div className="pb-2 pt-3 border-b border-gray-200 text-center flex items-center justify-center bg-indigo-200">
                                <Dialog.Title as="h3" className="text-lg font-semibold text-indigo-700">
                                    Dettagli Giorno {moment(formattedDay).format('DD/MM/YYYY')}
                                </Dialog.Title>
                            </div>
                            <div className='p-4'>
                                <div className='grid grid-cols-3 gap-2 w-full p-2'>
                                    <div className='col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1'>
                                        Dipendente
                                    </div>
                                    <div className='col-span-2 text-sm flex items-center'>
                                        {dipendente.last_name && dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}` : dipendente.cf}
                                    </div>
                                    {isEmployee && !hasViewShiftsPermission ? null : (
                                        <>
                                            <div className="col-span-1 font-semibold text-sm text-gray-600 uppercase pt-1">
                                                Fasce orarie lavoro
                                            </div>
                                            <div className="text-sm col-span-2">
                                                {employeeDayDetails?.shifts && employeeDayDetails.shifts.length > 0 ? (
                                                    <table className="table-auto w-full text-sm mb-2">
                                                        <thead>
                                                            <tr className="bg-gray-100">
                                                                <th className="px-2 py-1 text-left">Inizio Turno</th>
                                                                <th className="px-2 py-1 text-left">Fine Turno</th>
                                                                <th className="px-2 py-1 text-left">Inizio Pausa</th>
                                                                <th className="px-2 py-1 text-left">Fine Pausa</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {employeeDayDetails.shifts.map((shift, shiftIndex) =>
                                                                shift.time_slots.map((timeSlot, timeSlotIndex) => (
                                                                    <tr key={`${shiftIndex}-${timeSlotIndex}`} className="border-b">
                                                                        <td className="px-2 py-1">
                                                                            <div className="flex items-center gap-2">
                                                                                {shift.status === false && (
                                                                                    <CustomTooltip title="Turno da confermare" placement="top-start">
                                                                                        <div
                                                                                            className="w-3 h-3 bg-sky-300 rounded-full"
                                                                                            style={{ cursor: "pointer" }}
                                                                                        ></div>
                                                                                    </CustomTooltip>
                                                                                )}
                                                                                <span>{timeSlot.start_at || ''}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-2 py-1">{timeSlot.end_at || ''}</td>
                                                                        <td className="px-2 py-1">
                                                                            {timeSlot.breaks && timeSlot.breaks.length > 0
                                                                                ? timeSlot.breaks.map((breakItem, breakIndex) => (
                                                                                    <div key={breakIndex}>{breakItem.start_at || ''}</div>
                                                                                ))
                                                                                : 'Nessuna pausa'}
                                                                        </td>
                                                                        <td className="px-2 py-1">
                                                                            {timeSlot.breaks && timeSlot.breaks.length > 0
                                                                                ? timeSlot.breaks.map((breakItem, breakIndex) => (
                                                                                    <div key={breakIndex}>{breakItem.end_at || ''}</div>
                                                                                ))
                                                                                : 'Nessuna pausa'}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="text-sm text-gray-600">Nessun turno previsto</div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    {isEmployee && !hasViewTimbraturePermission ? null : (
                                        <>
                                            <div className='font-semibold text-sm text-gray-600 uppercase pt-1 col-span-1'>
                                                Timbrature
                                            </div>
                                            <div className='col-span-2'>
                                                {inouts && inouts.length > 0 ? (
                                                    <table className="table-auto w-full text-sm">
                                                        <thead>
                                                            <tr className="bg-gray-100">
                                                                <th className="px-2 py-1 text-left">Ingresso</th>
                                                                <th className="px-2 py-1 text-left">Uscita</th>
                                                                <th className="px-2 py-1 text-left">Posizione</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {inouts
                                                            .sort((a, b) => {
                                                                const dateA = a.checkin ? new Date(a.checkin) : new Date(0);
                                                                const dateB = b.checkin ? new Date(b.checkin) : new Date(0);
                                                                return dateA - dateB;
                                                            })
                                                            .map((inout, index) => {
                                                                const checkinDate = inout.checkin ? moment(inout.checkin).startOf('day') : null;
                                                                const checkoutDate = inout.checkout ? moment(inout.checkout).startOf('day') : null;
                                                                const today = formattedDay;
                                                                
                                                                return (
                                                                    <tr key={index} className="border-t">
                                                                        <td className="px-2 py-1">
                                                                            {inout && inout?.over_time_checkin ? (
                                                                                <RunningWithErrorsOutlinedIcon style={{ height: '20px', width: '20px', marginInlineEnd: '5px', color: '#ef4444'}} />
                                                                            ) : (
                                                                                <CheckCircleOutlineOutlinedIcon style={{ height: '20px', width: '20px', marginInlineEnd: '5px', color: '#22C55E'}} />
                                                                            )}
                                                                            {checkinDate
                                                                                ? checkinDate.isSame(today)
                                                                                    ? moment(inout.checkin).format('HH:mm')
                                                                                    : moment(inout.checkin).format('DD/MM/YYYY HH:mm')
                                                                                : '-'}
                                                                        </td>
                                                                        
                                                                        <td className="px-2 py-1">
                                                                        {inout && inout?.checkout ? (
                                                                            <>
                                                                            {inout?.over_time_checkout ? (
                                                                                <RunningWithErrorsOutlinedIcon style={{ height: '20px', width: '20px', marginInlineEnd: '5px', color: '#ef4444' }} />
                                                                            ) : (
                                                                                <CheckCircleOutlineOutlinedIcon style={{ height: '20px', width: '20px', marginInlineEnd: '5px', color: '#22C55E' }} />
                                                                            )}
                                                                            {checkoutDate && checkoutDate.isSame(today)
                                                                                ? moment(inout.checkout).format('HH:mm')
                                                                                : moment(inout.checkout).format('DD/MM/YYYY HH:mm')}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                            <PendingOutlinedIcon style={{ height: '20px', width: '20px', marginInlineEnd: '5px', color: '#f97316' }} />
                                                                            in corso
                                                                            </>
                                                                        )}
                                                                        </td>

                                                                        <td className="px-2 py-1">
                                                                            {(inout.checkin_lat && inout.checkin_lng) || (inout.checkout_lat && inout.checkout_lng) ? (
                                                                                <>
                                                                                    <span
                                                                                        className="text-indigo-800 cursor-pointer"
                                                                                        onClick={() => openLocationMap(inout)}
                                                                                    >
                                                                                        <MapPinIcon className="w-5 h-5 inline-block mr-1" /> Mappa
                                                                                    </span>
                                                                                </>
                                                                            ) : (
                                                                                <span className="text-sm text-gray-600">Posizione non registrata</span>
                                                                            )}
                                                                        </td>

                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                ) : (
                                                    <div className="text-sm text-gray-600">Nessuna timbratura disponibile</div>
                                                )}
                                            </div>
                                            {isLocationModalOpen && (  
                                                <div className='col-span-1 col-start-3 flex justify-end'>
                                                    <button
                                                        className="ml-4 uppercase font-semibold text-white text-xs px-2 py-1 rounded-md bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:bg-indigo-600"
                                                        onClick={closeLocationModal}
                                                    >
                                                        Chiudi Mappa
                                                    </button>
                                                </div>   
                                            )}
                                            {isLocationModalOpen && (
                                                <>
                                                    <div className='col-span-1'></div>
                                                    <div className='col-span-2' style={{ height: '70%' }}>
                                                        <div className="relative" style={{ height: '60vh', width: '100%', marginTop: '5px', marginBottom: '5px' }}>
                                                            <LocationMap locations={locationData} />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}

                                    {isEmployee && !hasManageJustificationPermission ? null : (
                                        <>
                                            <div className="font-semibold text-sm text-gray-600 uppercase pt-1 col-span-1">
                                                Giustificativi
                                            </div>
                                            {justification_requests && justification_requests.length > 0 ? (
                                                <>
                                                    <div className="col-span-2">
                                                        <table className="table-auto w-full text-sm">
                                                            <thead>
                                                                <tr className="bg-gray-100">
                                                                    <th className="px-2 py-1 text-left">Descrizione</th>
                                                                    <th className="px-2 py-1 text-left">Dalle Ore</th>
                                                                    <th className="px-2 py-1 text-left">Alle Ore</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {justification_requests
                                                                .sort((a, b) => new Date(a.datetime_from) - new Date(b.datetime_from))
                                                                .map((justification, index) => (
                                                                    <tr key={index} className="border-t">
                                                                        <td className="px-2 py-1">
                                                                            {justification.justification.justification.description}
                                                                        </td>
                                                                        <td className="px-2 py-1">
                                                                            {moment(justification.datetime_from).format('HH:mm')}
                                                                        </td>
                                                                        <td className="px-2 py-1">
                                                                        {moment(justification.datetime_to).format('HH:mm') === '00:00'
                                                                            ? '24:00'
                                                                            : moment(justification.datetime_to).format('HH:mm')}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-sm text-gray-600">Nessun giustificativo presente</span>  
                                            )}
                                        </>
                                    )}
                                </div>

                                <div className="mt-5 mb-2 sm:mt-6 flex justify-center">
                                    <button
                                        type="button"
                                        className="hover:bg-gray-100 w-1/2 text-xs flex justify-center focus:ring-indigo-500 items-center border border-gray-300 shadow-md rounded-md text-gray-500 font-bold btn-xs p-2"
                                        onClick={onClose}
                                    >
                                        CHIUDI
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
             </Dialog>
        </Transition.Root>
    );
};

export default DayDetailsModal;
